import { mapState, mapGetters, mapActions } from 'vuex';
import scopeBreadcrumbMixin from '@/components/core/tenant/scopeBreadCrumbMixin';
import flatten from 'lodash/flatten';

export default {
  mixins: [scopeBreadcrumbMixin],
  data: function () {
    return {
      scopeDescencionMapping: {},
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'color', 'UiTheme', 'permissions']),
    ...mapState('authentication', ['user', 'isImpersonating']),
    ...mapGetters({
      sessionAccess: 'authentication/access',
    }),
    hasImpersonatePermission() {
      return this.permissions['all.manage'] || this.permissions['UserManagement.impersonate'];
    },
    hasUserManagePermission() {
      return this.permissions['UserManagement.manage'];
    },
    sessionAccessUserManage() {
      return this.sessionUserFilteredAccesses('manage');
    },
    sessionAccessUserImpersonate() {
      return this.sessionUserFilteredAccesses('impersonate');
    },
  },
  methods: {
    ...mapActions('authentication', ['impersonate']),
    doFetchSessionAccessDetails() {
      // fetch all scope-details for the user's acesses.
      // this includes all ancestor-info for all scopes as well
      return Promise.all(
        this.sessionAccess.map((access) => {
          return this.fetchScopeDetails({ id: access.scopeId });
        })
      ).catch((error) => {
        console.error(error);
      });
    },
    canManageUser(user) {
      if (!this.hasUserManagePermission) {
        return false;
      }
      if (this.permissions['super.system.manage']) {
        return true;
      }
      return this.matchAllAccesses(user.access || [], 'manage');
    },
    canImpersonateUser(user) {
      if (!this.hasImpersonatePermission) {
        return false;
      }
      if (this.permissions['super.system.manage']) {
        return true;
      }
      return this.matchAllAccesses(user.access || [], 'impersonate');
    },
    sessionUserFilteredAccesses(userManagementPermission = 'manage') {
      return this.sessionAccess.filter((a) => {
        return a.roles.find((role) => {
          return role.Rules.find((rule) => {
            return (
              (rule.actions.indexOf('manage') >= 0 && rule.subject.indexOf('all') >= 0) ||
              (rule.actions.indexOf(userManagementPermission) >= 0 && rule.subject.indexOf('UserManagement') >= 0)
            );
          });
        });
      });
    },
    matchAllAccesses(userAccess, permission) {
      const primaryAccesses = userAccess.filter((a) => a.primary);
      const sessionAdminAccesses =
        permission === 'impersonate' ? this.sessionAccessUserImpersonate : this.sessionAccessUserManage;

      // current user needs the permission (or all.manage) on each of those primary scopes
      // first check if there is a direct match, i.e. no descendent scope
      const notMatchingPrimaryAccesses = primaryAccesses.filter((access) => {
        return !sessionAdminAccesses.find((a) => {
          return a.scopeId === access.scopeId;
        });
      });
      if (notMatchingPrimaryAccesses.length === 0) {
        return true;
      }

      const isDescendentOf = (adminScopeId, userScopeId) => {
        if (this.scopeDescencionMapping[`${adminScopeId}_${userScopeId}`] !== undefined) {
          return this.scopeDescencionMapping[`${adminScopeId}_${userScopeId}`];
        }
        // find all direct children of adminScopeId
        // use the cached version of `scopeDetails`, which contains info of all scopes for the session-user,
        // and all scopes of all accesses of the current displayed users
        const childScopes = Object.values(this.scopeDetails).map((details) => {
          return details.hierarchy.filter((e) => e.parentId === adminScopeId);
        });
        const childScopeIds = flatten(childScopes).map((s) => s.id);
        if (childScopeIds.find((s) => s === userScopeId)) {
          this.scopeDescencionMapping[`${adminScopeId}_${userScopeId}`] = true;
          return true;
        }
        const result = childScopeIds.some((s) => {
          return isDescendentOf(s, userScopeId);
        });
        this.scopeDescencionMapping[`${adminScopeId}_${userScopeId}`] = result;
        return result;
      };
      return notMatchingPrimaryAccesses.every((access) => {
        return sessionAdminAccesses.find((a) => {
          return isDescendentOf(a.scopeId, access.scopeId);
        });
      });
    },
    doImpersonateUser(user) {
      this.impersonate(user.id)
        .then(() => {
          // hard reload the page with the new credentials
          // do a whole page reload, to clear the permission "cache" (i.e. the vue-store)
          window.location.reload();
        })
        .catch((error) => {
          console.error('ERROR IMPERSONATING', error);
          this.$snack(this.$i18n_t('Common.errorboundary.title'), 'error');
        });
    },
  },
};
